import React, { useState } from 'react';
import { Constants } from "@eagerdog/constants";
import { apiService } from "src/services";

import { toast } from "src/components/Toast/ToastManager";
import { Modal, useModal } from "src/components/Modal/Modal";
import Confirm from "src/components/Confirm/Confirm";
import ContextMenu, { IContextMenuOption } from "src/components/ContextMenu/ContextMenu";
import MoveUp from "src/components/MoveUp/MoveUp";
import EnterResults from "src/components/EnterResults/EnterResults";
import AssignArmband from "src/components/AssignArmband/AssignArmband";
import { useMessages } from "src/context/MessagesContext";

import type { CustomCellRendererProps } from 'ag-grid-react';

import styles from "./ResultsTab.module.scss";
import { IEventMessage, IEventSummary, IShow } from "@eagerdog/interfaces";
import SendMessage, { SendMessageType } from "../../../components/SendMessage/SendMessage";
import { download } from "../../../components/Download/DownloadManager";

interface CustomResultsCellRendererProps extends CustomCellRendererProps {
  getResults(): void
  event: IEventSummary
  shows?: IShow[]
  scores?: any[]
  onShowsChange?(shows: IShow[]): void
}

enum ActionsRendererMenuOptions {
  Move = "move",
  Enter = "enter",
  Email = "email",
  AssignArmband = "assignArmband",
}

export const ActionsRenderer = (props: CustomResultsCellRendererProps) => {
  const [modalContent, setModalContent] = useState<ActionsRendererMenuOptions | undefined>();
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);

  const { isShown, toggle } = useModal();
  const { initiateConversation } = useMessages();

  let noMoveUp:string[] = [
    Constants.show_type.fast_cat
  ];

  const getShowData = () => {
    if (props.shows && props.value.showId) {
      return props.shows.find(show => show._id === props.value.showId);
    }
    return undefined;
  };

  const emailResults = (payload: IEventMessage) => {
    setIsSending(true)
    const content = payload.is_test_sending
      ? `Test email sent to ${props.value.event.club.email}.`
      : "You've successfully emailed the individual results"

    apiService.requestResultsEmailForShowRegistration(props.value.event._id, props.value.result._id, payload).then((response) => {
      toast.show({
        title: "Email Result",
        content,
        duration: 10000,
        type: "success"
      });
    }).catch((e) => {
      toast.show({
        title: "Reset Password",
        content: e.response.data.message ? "There is no account with that email" : "Something went wrong, please try again later",
        duration: 10000,
        type: "fail"
      });
    }).finally(() => {
      setIsSending(false)
      toggle();
      setModalContent(undefined);
    });
  }

  if (!props.value || !props.data) {
    return null;
  }

  let menuOptions:IContextMenuOption[] = [];
  if (!noMoveUp.includes(props.data.element)) {
    menuOptions.push({
      label: "Move Up",
      onClick:() => {
        setModalContent(ActionsRendererMenuOptions.Move);
        toggle();
      }
    });
  }

  if (props.value.canEnterResults) {
    menuOptions.push({
      label: "Enter Results",
      onClick:() => {
        setModalContent(ActionsRendererMenuOptions.Enter);
        toggle();
      }
    });

    if (props.data.result !== "No Result") {
      menuOptions.push({
        label: "Clear Results",
        onClick: () => setShowConfirm(true)
      });
    }

    menuOptions.push({
      label: "Email Results",
      onClick:() => {
        setModalContent(ActionsRendererMenuOptions.Email);
        toggle();
      }
    });
  }

  if (props.value.canDownloadIndividualResults) {
    menuOptions.push({
      label: "Download Scoresheet",
      onClick:() => {
        // For UKC events, use ukc_scoresheets with scoreId filter
        // For AKC Fetch, use the individual_scoresheet endpoint
        const isUKC = props.value.event.sanctioning_club === Constants.sanctioning_club.UKC;
        
        let _downloadOptions: any = {
          type: isUKC ? "ukc_scoresheets" : "individual_scoresheet",
          eventId: props.value.event._id,
          showId: props.value.showId,
          scoreId: props.value.result._id,
          builder_suffix: "2"
        };
        download.pushDownload(_downloadOptions);
      }
    });
  }

  // "Send Message" — always available so secretaries can reach owners regardless of results state.
  // Uses dog_id so the backend resolves the owner's user account.
  if (props.data?.dog_id) {
    menuOptions.push({
      label: "Send Message",
      onClick: () => {
        initiateConversation(props.data.dog_id, props.data.ownerName);
      },
    });
  }

  // Unlike the Registrations tab's version of this action, this only edits
  // the armband number for this one run (score) - it doesn't cascade to the
  // dog's other runs or its registration record.
  menuOptions.push({
    label: "Assign / Edit Armband",
    onClick: () => {
      setModalContent(ActionsRendererMenuOptions.AssignArmband);
      toggle();
    }
  });

  menuOptions.push({
    label: "Move to Waitlists",
    onClick:() => {
      apiService.returnScoreToWaitlist(props.value.event._id, props.value.result._id)
        .then(() => {
          props.getResults();
          toast.show({
            title: "Move to Waitlists",
            content: "You've successfully returned back the entry to the waitlists",
            duration: 10000,
            type: "success"
          });
        })
        .catch((error) => {
          toast.show({
            title: "Move to Waitlists",
            content: error.response.data.message ??  "Something went wrong, please try again later",
            duration: 10000,
            type: "fail"
          });
        })
    }
  });

  const getModalClassName = () => {
    switch(modalContent) {
      case ActionsRendererMenuOptions.Move:
        return "moveUpModal";
      case ActionsRendererMenuOptions.Enter:
        return "enterResultsModal";
      case ActionsRendererMenuOptions.Email:
        return "sendMessageModal";
      case ActionsRendererMenuOptions.AssignArmband:
        return "assignArmbandModal";
    }

    return "";
  }

  const getModalContent = () => {
    switch(modalContent) {
      case ActionsRendererMenuOptions.Move:
        return <MoveUp
            event={props.value.event}
            registration={props.value.result}
            onComplete={() => {
                toggle();
                setModalContent(undefined);
                props.getResults();
              }
            }
            onClose={() => {
                toggle();
                setModalContent(undefined);
                props.getResults();
              }
            }
          />;
      case ActionsRendererMenuOptions.Enter:
        return <EnterResults
          event={props.value.event}
          eventId={props.value.event._id}
          showType={props.value.result.show_type}
          result={props.value.result}
          show={getShowData()}
          showScores={(props.scores || []).filter((s: any) => s.show_id === props.value.showId)}
          onSave={async () => {
            toggle();
            setModalContent(undefined);
            
            // For scentwork, the server writes the first result's hides/time
            // limit back onto the judge-schedule tile. Push the refreshed shows
            // up to the grid owner so the next dog's form sees them; the
            // response was previously discarded and the tiles stayed stale.
            if (props.value.result.show_type === Constants.show_type.scentwork) {
              try {
                const shows = await apiService.getClubEventShows(props.value.event.club._id, props.value.event._id);
                props.onShowsChange?.(shows);
              } catch (e) {
                console.error("Failed to refresh shows after results save:", e);
              }
            }
            
            props.getResults();
          }}
          onClose={() => {
            toggle();
            setModalContent(undefined);
            props.getResults();
          }}
        />;
      case ActionsRendererMenuOptions.Email:
        const eventDate = new Intl.DateTimeFormat("en-US", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          timeZone: props.event.timezone || "America/Toronto",
        }).format(new Date(props.event.start_date))

        return <SendMessage
          isSending={isSending}
          handleSendMessage={emailResults}
          subject={`${props.event.title} Results - ${eventDate}`}
          messageBody={`
          <p>Congratulations on completing another great event!</p>
          <p>Please find your results attached for all dogs you competed with.</p>
          <p>${props.event.club.club_name} - ${props.event.club.email} - ${props.event.club.phone}</p>
        `}
          type={SendMessageType.EmailResults}
          eventId={props.event._id}
        />
      case ActionsRendererMenuOptions.AssignArmband:
        return <AssignArmband
          event={props.value.event}
          scoreId={props.value.result._id}
          registration={{
            show_id: props.value.result.show_id,
            show_type: props.value.result.show_type,
            show_name: props.value.result.show_name,
            arm_number: props.value.result.arm_number,
            dog: {
              dog_id: props.value.result.dog_id,
              call_name: props.value.result.call_name,
              registered_name: props.value.result.registered_name,
            }
          }}
          rowData={props.data}
          onComplete={() => {
            toggle();
            setModalContent(undefined);
            props.getResults();
          }}
          onClose={() => {
            toggle();
            setModalContent(undefined);
          }}
        />
    }

    return <></>;
  }

  const handleClearResult = () => {
    apiService.deleteScoreResult(props.value.event._id, props.value.result._id)
      .then(() => {
        props.getResults();
        toast.show({
          title: "Clear Result",
          content: "You've successfully cleared the individual results",
          duration: 10000,
          type: "success"
        });
      })
      .catch((error) => {
        toast.show({
          title: "Clear Result",
          content: error.response.data.message ??  "Something went wrong, please try again later",
          duration: 10000,
          type: "fail"
        });
      })
      .finally(() => setShowConfirm(false));
  }

  return (<div className={styles.ActionRenderer}>
    <ContextMenu
      options={menuOptions}
    />
    <Modal
      className={getModalClassName()}
      isShown={isShown}
      hide={toggle}
      modalContent={getModalContent()}
    />
    <Modal className="Confirmation" isShown={showConfirm} hide={() => { setShowConfirm(false); }}
      modalContent={
        <Confirm
          message={<><p>Are you sure you want to clear this result?</p></>}
          onConfirm={handleClearResult}
          onCancel={() => { setShowConfirm(false); }}
        />
      }
    />
  </div>);
};